/* SOV — Facets Distributor (root, availability=1, session-scoped)
   Versione: v6 – 2025-10-25 (Europe/Rome)

   Obiettivi:
   - fetch unico per sessione/lingua (dedupe tra moduli e tra script duplicati)
   - cache in sessionStorage (TTL 30m) con fallback "stale" in caso di errore
   - MAI mettere in cache risposte di errore / JSON non valido
   - evento: 'sov:facets:root:ready' { detail: { payload } }
*/

(function () {
  'use strict';

  /* ===========================
     namespace & feature flags
     =========================== */
  window.SOV = window.SOV || {};
  SOV.facets = SOV.facets || {};
  window.SHOW_VENDORS = (typeof window.SHOW_VENDORS === 'boolean') ? window.SHOW_VENDORS : false;

  /* persist dedupe & last payload across reiniezioni dello script */
  var shared = SOV.facets.__shared = SOV.facets.__shared || {
    inFlight: null,
    lastPayload: null
  };

  /* ===========================
     helpers di base
     =========================== */

  /* estrae prefisso locale dal path: "ll-cc", "ll", oppure "default" */
  function getLocaleFromPath() {
    try {
      var p = location.pathname || '';
      var m = p.match(/^\/([a-z]{2})(?:-([a-z]{2}))?(?:\/|$)/i);
      if (!m) return 'default';
      var ll = (m[1] || '').toLowerCase();
      var cc = (m[2] || '').toLowerCase();
      return cc ? (ll + '-' + cc) : ll;
    } catch (_) { return 'default'; }
  }

  /* endpoint locale-aware; forza availability=1 */
  function buildRootEndpoint() {
    try {
      var loc = getLocaleFromPath();
      var prefix = loc && loc !== 'default' ? '/' + loc : '';
      return prefix + '/collections/all?view=sov-facets.json&filter.v.availability=1';
    } catch (_) {
      return '/collections/all?view=sov-facets.json&filter.v.availability=1';
    }
  }

  /* JSON safe: marca qualsiasi errore con __sov_error per evitare cache */
  function safeJsonOrError(r) {
    if (!r || !r.ok) {
      return Promise.resolve({ __sov_error: true, __http_status: r ? r.status : 0, filters: [] });
    }
    return r.json().then(
      function (j) { return j; },
      function () { return { __sov_error: true, __http_status: r.status, filters: [] }; }
    );
  }

  function isValidRootPayload(json) {
    return !!(json && Array.isArray(json.filters));
  }

  function shouldCache(json) {
    // valido e non marcato come errore
    return isValidRootPayload(json) && !json.__sov_error;
  }

  function dispatchReady(payload) {
    try {
      shared.lastPayload = payload;
      var ev = new CustomEvent('sov:facets:root:ready', { detail: { payload: payload } });
      document.dispatchEvent(ev);
    } catch (_) { /* no-op */ }
  }

  /* ===========================
     session cache (TTL 30m)
     =========================== */
  var TTL_MS = 30 * 60 * 1000; // 30 minuti

  function cacheKey() {
    return 'sov_facets_v6_' + getLocaleFromPath() + '_inStock';
  }

  function readCache(allowStale) {
    try {
      var raw = sessionStorage.getItem(cacheKey());
      if (!raw) return null;
      var box = JSON.parse(raw);
      if (!box || typeof box.ts !== 'number' || !isValidRootPayload(box.data)) return null;
      if (!allowStale && (Date.now() - box.ts) > TTL_MS) return null;
      return box.data;
    } catch (_) { return null; }
  }

  function writeCache(json) {
    try {
      sessionStorage.setItem(cacheKey(), JSON.stringify({ ts: Date.now(), data: json }));
    } catch (_) { /* no-op */ }
  }

  /* ===========================
     API pubblica
     =========================== */

  /**
   * getRootSync(): ritorna sincrono dalla cache (valida) o null.
   */
  SOV.facets.getRootSync = function () {
    return readCache(false);
  };

  /**
   * onReady(fn): chiama fn subito se payload già disponibile, altrimenti la chiama al prossimo dispatch.
   * (Compat helper usato da alcuni moduli.)
   */
  SOV.facets.onReady = function (fn) {
    if (typeof fn !== 'function') return;
    if (shared.lastPayload) {
      try { fn(shared.lastPayload); } catch (_) {}
    } else {
      var handler = function (e) {
        try { fn(e.detail && e.detail.payload); } catch (_) {}
        document.removeEventListener('sov:facets:root:ready', handler);
      };
      document.addEventListener('sov:facets:root:ready', handler);
    }
  };

  /**
   * ensureRoot():
   *  - se cache valida → resolve subito + dispatch
   *  - altrimenti un solo fetch dedupicato, cache SOLO se payload valido,
   *    dispatch sempre (anche in errore con fallback stale o payload vuoto)
   */
  SOV.facets.ensureRoot = function () {
    var cached = readCache(false);
    if (isValidRootPayload(cached)) {
      dispatchReady(cached);
      return Promise.resolve(cached);
    }

    if (shared.inFlight) return shared.inFlight;

    var url = buildRootEndpoint();

    shared.inFlight = fetch(url, { credentials: 'same-origin', cache: 'no-store' })
      .then(safeJsonOrError)
      .then(function (json) {
        // Se valido → cache & dispatch
        if (shouldCache(json)) {
          if (!json.hints) json.hints = {};
          json.hints.locale_prefix = getLocaleFromPath();
          writeCache(json);
          dispatchReady(json);
          return json;
        }

        // Qui: HTTP non-OK, JSON rotto, o payload non valido
        var stale = readCache(true); // permettiamo stale
        if (isValidRootPayload(stale)) {
          if (!stale.hints) stale.hints = {};
          stale.hints.locale_prefix = getLocaleFromPath();
          // NON riscriviamo la cache; semplicemente consegniamo lo stale
          dispatchReady(stale);
          return stale;
        }

        // Nessuna cache utilizzabile → dispatch vuoto ma NON cache
        var empty = { filters: [], hints: { locale_prefix: getLocaleFromPath(), error: true } };
        dispatchReady(empty);
        return empty;
      })
      .catch(function () {
        // Anche errori di rete/JS arrivano qui → stesso fallback stale/empty
        var stale = readCache(true);
        if (isValidRootPayload(stale)) {
          if (!stale.hints) stale.hints = {};
          stale.hints.locale_prefix = getLocaleFromPath();
          dispatchReady(stale);
          return stale;
        }
        var empty = { filters: [], hints: { locale_prefix: getLocaleFromPath(), error: true } };
        dispatchReady(empty);
        return empty;
      })
      .finally(function () { shared.inFlight = null; });

    return shared.inFlight;
  };

  /* se qualche modulo ha già bisogno subito, proviamo a servire cache o triggerare il fetch */
  // (non obbligatorio: lasciamo che i consumatori chiamino ensureRoot)
})();
